<template>
  <CollapseGroup id="menu" tag="ul" class="list alternative-focus-outline">
    <Component
      :is="withSublist(item) ? Collapse : 'li'"
      v-bind="withSublist(item) ? { tag: 'li', id: `menu_${i}` } : {}"
      v-for="(item, i) in items"
      :key="i"
      ref="listitem"
      :class="{
        'list-item--with-sublist': withSublist(item),
        'list-item--last': i === items.length - 1
      }"
      class="list-item"
    >
      <CollapseTitle v-if="withSublist(item)" class="collapse-title">
        <slot name="link" el-class="link" :item="item" />
      </CollapseTitle>
      <slot v-else name="link" el-class="link" :item="item" />

      <CollapseContent
        v-if="withSublist(item)"
        v-slot="{ isActive }"
        class="collapse"
      >
        <Component :is="forceSublist ? 'div' : 'ul'">
          <Component
            :is="forceSublist ? 'div' : 'li'"
            v-for="(subItem, index) in item.items"
            :key="index"
            class="sub-link-item"
            :class="{
              'sub-link-item--last': index === (item.items?.length || 1) - 1
            }"
          >
            <slot
              el-class="sub-link"
              :sub-item="subItem"
              :is-list-active="isActive"
            />
          </Component>
        </Component>
      </CollapseContent>
    </Component>
  </CollapseGroup>
</template>

<script setup lang="ts">
import type {
  PublicMenuItem,
  PublicSecondLevelMenuItem
} from '@/service/__generated-api'

const props = withDefaults(
  defineProps<{
    items: PublicMenuItem[]
    forceSublist?: boolean
  }>(),
  { forceSublist: false }
)

const Collapse = resolveComponent('Collapse')
const CollapseContent = resolveComponent('CollapseContent')

const withSublist = (item: PublicMenuItem | PublicSecondLevelMenuItem) =>
  ('items' in item && item.items?.length) || props.forceSublist
</script>

<style lang="scss" scoped>
.list {
  width: 100%;
}

.list-item {
  @include flex-col;

  &--with-sublist:deep(.link) {
    display: flex;
    gap: rem(10px);
    align-items: center;
    justify-content: space-between;

    &::after {
      display: block;
      width: rem(16px);
      height: rem(16px);
      margin-left: rem(8px);
      content: '';
      background-image: url('@/assets/icons/chevron-down.svg');
      background-repeat: no-repeat;
      background-position: 50% 50%;
      background-size: contain;

      transition: transform $transition;
    }
  }

  &--last {
    padding-bottom: 0;
  }

  &:deep(.link) {
    position: relative;
    padding-block: rem(11px) rem(12px);
    border-top: 1px solid $layout-divider;

    @include media-up(md) {
      padding-block: rem(13px) rem(14px);
    }
  }

  &:deep(.link),
  &:deep(.sub-link) {
    @include focus-visible;
  }
}

:deep(.input:checked) ~ .collapse-title .link::after {
  transform: rotate(-180deg);
}

.collapse {
  width: 100%;
}
</style>
